import React from 'react';
import { DialogActions, DialogContent } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import cx from 'classnames';
// eslint-disable-next-line import/no-named-as-default -- posthog-js re-exports its own default as a named `posthog` too
import posthog from 'posthog-js';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../../common/launchDarkly';
import { useIsSkySlopeMobileApp } from '../../common/utils';
import { SmartEmailContextProvider, useSmartEmailContext } from '../../context/smart-email-context';
import { useUpdateExtendedTrialEnvelopes } from '../../hooks/accounts-api';
import { useDS3Access } from '../../hooks/trial/use-ds3-access';
import { useEmailDefaults } from '../../hooks/use-email-defaults';
import {
  SUBSCRIPTION_RESTRICTED_EMAIL_BODY_LENGTH,
  useEmailLengthRestriction,
} from '../../hooks/use-email-length-restriction';
import { EMAIL_BODY_LENGTH, EMAIL_SUBJECT_LENGTH } from '../../lib/constants';
import { useIsMediumScreenMediaQuery } from '../../lib/isSmallScreen';
import { useShallowSelector, useDispatch } from '../../lib/reduxHooks';
import { generateEmailSubject, generateEmailBody } from '../../lib/utils';
import validateBlocksAndSendEnvelope from '../../lib/validateBlocksAndSendEnvelope';
import { focusInput, blurInput, validateBlocksBeforeSending, updateEmailConfig } from '../../store/senderBuild/actions';
import type { IEmailConfig } from '../../store/senderBuild/types';
import { postMessageToMobileApp } from '../../utils/post-message';
import EmailBodyLoading from './EmailBodyLoading';
import { EmailLengthCounter } from './EmailLengthCounter';
import { EmailPaywallBanner } from './EmailPaywallBanner';
import { SmartEmail } from './SmartEmail';

const useStyles = makeStyles(() => ({
  mobileTitle: {
    fontFamily: 'proxima-nova, sans-serif !important',
  },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: '56px',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24.5px',
    marginBottom: '24.5px',
  },
  mobileButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    bottom: 0,
    position: 'fixed',
    width: '100%',
    left: 0,
    alignItems: 'center',
    padding: '15px 0',
    borderTop: '1px solid #CAD5E0',
    backgroundColor: '#FFF',
  },
  inputContent: {
    width: '100%',
    paddingBottom: '24px',
  },
  helperText: {
    textAlign: 'right',
    backgroundColor: '#F5F7F9',
    marginTop: 0,
  },
}));

// we're not using most of the props specifically for isMobileApp
interface IProps {
  flags: LaunchDarklyFlags;
  fullScreen?: boolean;
  onClose?: () => void;
  subject?: string;
  action?: string;
  send?: boolean;
  handleBackButtonTap?: () => void;
  sendEnvelopeForMobileApp?: (emailConfig: { subject: string; body: string }) => void;
  envelopeId?: string;
}

export type SmartEmailConfig = {
  tone: string;
  body: string;
  envelopeId: string;
};

function ActionButtons({
  onClose,
  saveEmailConfig,
  emailConfig,
  action,
  fullScreen,
  disabled = false,
}: {
  onClose: any;
  saveEmailConfig: any;
  emailConfig: IEmailConfig;
  action: any;
  fullScreen: boolean;
  disabled?: boolean;
}) {
  const classes = useStyles();
  return (
    <div className={fullScreen ? classes.mobileButtonWrapper : classes.buttonWrapper}>
      <Button
        data-spec="cancel"
        variant={fullScreen ? 'outlined' : 'text'}
        style={{ marginRight: fullScreen ? '16px' : '24px', width: fullScreen ? '140px' : '' }}
        color="primary"
        onClick={onClose}
      >
        {fullScreen ? 'Back' : 'Cancel'}
      </Button>
      <Button
        data-spec="saveButton"
        variant="contained"
        color="primary"
        onClick={saveEmailConfig}
        disabled={emailConfig.loading || disabled}
        style={{ width: fullScreen ? '140px' : '' }}
      >
        {action}
      </Button>
    </div>
  );
}

function handleCharacterLimit(e: any, inputCharacterLimit: number) {
  const { clipboardData } = e;
  if (clipboardData) {
    if (e.target.value.length + e.clipboardData.getData('Text').length > inputCharacterLimit) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
  if (e.target.value.length >= inputCharacterLimit) {
    e.preventDefault();
    e.stopPropagation();
  }
}

function helperText(props: { textLength: number; maxCharacters: number }) {
  const diff = props.maxCharacters - props.textLength;
  if (props.textLength >= props.maxCharacters) {
    return 'Max character count reached.';
  }
  if (diff <= 30) {
    return `${diff} Characters remaining`;
  }
  return '';
}

function EditEmailModal(props: IProps) {
  const { flags, onClose = () => {} } = props;
  const classes = useStyles();
  const { emailConfig, id, blocks, blockGroups, zoom, hasAttachment } = useShallowSelector((state) => ({
    emailConfig: state.senderBuild.emailConfig,
    id: state.senderBuild.id,
    blocks: state.senderBuild.blocks,
    blockGroups: state.senderBuild.groups,
    zoom: state.senderBuild.zoom,
    hasAttachment: state.pageFrame.settings?.user?.emailDocsAsAttachment,
  }));

  const [emailBody, setEmailBody] = React.useState('');
  const [emailSubject, setEmailSubject] = React.useState('');
  const dispatch = useDispatch();
  const isMobile = useIsMediumScreenMediaQuery();
  const isMobileApp = useIsSkySlopeMobileApp();
  const showSmartEmail = flags?.['show-smart-email'];
  const { mutateAsync: updateEnvelopesLeft } = useUpdateExtendedTrialEnvelopes();
  const { hasDs3Access, latestTrialExtension } = useDS3Access(flags);
  const { isEmailLengthLimitedBySubscription, emailLength, emailLengthPercent, isEmailAtLimit } =
    useEmailLengthRestriction(flags, emailBody);
  const emailBodyLimit = isEmailLengthLimitedBySubscription
    ? SUBSCRIPTION_RESTRICTED_EMAIL_BODY_LENGTH
    : EMAIL_BODY_LENGTH;

  const { loading: smartEmailLoading } = useSmartEmailContext();
  const emailDefaults = useEmailDefaults('ReviewAndSign');

  const emailSubjectPlaceholder = emailDefaults.data?.subject || generateEmailSubject();
  const emailBodyPlaceholder = emailDefaults.data?.body || generateEmailBody();

  React.useEffect(() => {
    if (isMobileApp) {
      postMessageToMobileApp({
        header_text: showSmartEmail ? 'Save time with smart emails' : 'Ready to send?',
        back_button_type: '<',
        should_back_button_exit: false,
        button_text: 'Send',
      });
    }
    window.mobileApp.nextButtonTap = () => {};
    window.mobileApp.backButtonTap = () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  window.mobileApp.backButtonTap = () => {
    if (props.handleBackButtonTap != undefined) props.handleBackButtonTap();
  };

  window.mobileApp.nextButtonTap = () => {
    if (props.sendEnvelopeForMobileApp)
      props.sendEnvelopeForMobileApp({ subject: emailSubject.trim() || '', body: emailBody.trim() || '' });
  };

  async function saveEmailConfigAndSometimesSend() {
    const sendEnvelope = props.send;
    if (sendEnvelope) {
      const updatedBlocksAndGroups = validateBlocksAndSendEnvelope(blocks, blockGroups, sendEnvelope, zoom);
      if (updatedBlocksAndGroups) {
        posthog.capture('envelope_send_requested', {
          has_attachment: hasAttachment ?? false,
          has_custom_email_message: Boolean(emailSubject.trim() || emailBody.trim()),
        });
        dispatch(
          validateBlocksBeforeSending(
            updatedBlocksAndGroups.updatedBlocks,
            updatedBlocksAndGroups.updatedGroups,
            { subject: emailSubject.trim() || '', body: emailBody.trim() || '', hasAttachment: hasAttachment ?? false },
            sendEnvelope
          )
        );

        if (!hasDs3Access && (latestTrialExtension?.envelopesLeft ?? 0) > 0) {
          await updateEnvelopesLeft();
        }
      }
    } else {
      dispatch(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        updateEmailConfig(id, { subject: emailSubject.trim() || '', body: emailBody.trim() || '' }, sendEnvelope)
      );
    }
    onClose();
  }

  function onFocus() {
    dispatch(focusInput());
  }
  function onBlur() {
    dispatch(blurInput());
  }

  React.useEffect(() => {
    setEmailSubject(emailConfig.subject || '');
    setEmailBody(emailConfig.body || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  return (
    <SmartEmailContextProvider>
      <Dialog
        open
        onClose={onClose}
        classes={{ paper: `${isMobile ? '' : 'max-w-[824px]'} px-4 md:px-10 overflow-y-auto` }}
        // @ts-expect-error -- This is for testing purposes
        PaperProps={{ 'data-spec': 'editEmailModal' }}
        maxWidth="lg"
        fullWidth
        fullScreen={props.fullScreen}
        BackdropProps={{ style: { backgroundColor: '#3f5b77', opacity: 0.6 } }}
      >
        <DialogContent>
          <div className="flex justify-between items-center py-4 md:py-6">
            {props.subject && (
              <Typography classes={{ h3: isMobile ? classes.mobileTitle : '' }} variant="h3">
                {props.subject}
              </Typography>
            )}
            {!isMobile && !isMobileApp && (
              <IconButton data-spec="close" onClick={onClose} className="text-grey-500 absolute top-2 right-2">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          <TextField
            label="Email Subject Line"
            variant="filled"
            data-spec="editableSubject"
            placeholder={emailSubjectPlaceholder}
            onKeyPress={(e) => handleCharacterLimit(e, EMAIL_SUBJECT_LENGTH)}
            onPaste={(e) => handleCharacterLimit(e, EMAIL_SUBJECT_LENGTH)}
            className={classes.inputContent}
            error={emailSubject.length >= EMAIL_SUBJECT_LENGTH}
            helperText={helperText({ textLength: emailSubject.length, maxCharacters: EMAIL_SUBJECT_LENGTH })}
            FormHelperTextProps={{ classes: { root: classes.helperText } }}
            onChange={(e) => setEmailSubject(e.target.value)}
            value={emailSubject}
            disabled={emailConfig.loading || emailDefaults.isLoading}
            onFocus={onFocus}
            onBlur={onBlur}
          />

          {showSmartEmail && props.envelopeId && (
            <SmartEmail emailBody={emailBody} setEmailBody={setEmailBody} envelopeId={props.envelopeId} />
          )}
          <div className="relative mb-[200px] md:mb-0">
            {smartEmailLoading && <EmailBodyLoading />}
            <TextField
              id="email-body"
              label={showSmartEmail ? undefined : 'Email Message'}
              multiline
              inputProps={{
                className: 'resize-y min-h-[150px]',
                onKeyPress: (e) => handleCharacterLimit(e, emailBodyLimit),
                onPaste: (e) => handleCharacterLimit(e, emailBodyLimit),
              }}
              InputProps={{
                rowsMin: 3,
                className: cx({
                  '!pb-[28px]': isEmailLengthLimitedBySubscription,
                  '!border-l-2 !border-r-2 !border-l-red-400 !border-r-red-400': emailBody.length >= emailBodyLimit,
                }),
              }}
              InputLabelProps={{ className: 'pb-2' }}
              className="pb-7.5 w-full"
              variant="filled"
              data-spec="editableBody"
              placeholder={emailBodyPlaceholder}
              error={emailBody.length >= emailBodyLimit}
              helperText={
                // when email is limited by subscription we display limit without helper text
                isEmailLengthLimitedBySubscription
                  ? undefined
                  : helperText({ textLength: emailBody.length, maxCharacters: emailBodyLimit })
              }
              FormHelperTextProps={{ classes: { root: classes.helperText } }}
              onChange={(e) => setEmailBody(e.target.value)}
              value={emailBody}
              disabled={emailConfig.loading || emailDefaults.isLoading}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            {isEmailLengthLimitedBySubscription && (
              <EmailLengthCounter
                emailLength={emailLength}
                emailLengthPercent={emailLengthPercent}
                isEmailAtLimit={isEmailAtLimit}
              />
            )}
          </div>
          {isEmailLengthLimitedBySubscription && isEmailAtLimit && <EmailPaywallBanner />}

          {showSmartEmail && !isMobileApp ? (
            <div className="flex flex-grow gap-3 py-3" data-spec="aiContentText">
              <InfoIcon className="w-[18px] h-[18px] fill-white stroke-grey-700 " />
              <div className="text-grey-700">
                Content generated using AI. Review the content for accuracy and completeness prior to use.
              </div>
            </div>
          ) : null}
        </DialogContent>

        {!isMobileApp && (
          <DialogActions>
            <ActionButtons
              onClose={onClose}
              action={props.action ? props.action : ''}
              saveEmailConfig={saveEmailConfigAndSometimesSend}
              emailConfig={emailConfig}
              fullScreen={isMobile}
              disabled={emailBody.length > emailBodyLimit}
            />
          </DialogActions>
        )}
      </Dialog>
    </SmartEmailContextProvider>
  );
}

const withLd = withLaunchDarkly(EditEmailModal);
export { withLd as EditEmailModal };
